import { uploadFile, ContentPolicyError } from '../../services/imageUploadService';

/**
 * Service for handling post media (images and videos) uploads to S3.
 * All uploads go through the Core API (/exsportscoreapi/upload) so that
 * Rekognition moderation is applied consistently with other asset types.
 */
class PostImageService {
  /**
   * Upload multiple images or videos for a post
   * @param {File[]} files - The media files to upload
   * @param {string} postId - The post's unique identifier (used to group this post's images)
   * @param {string} userId - The authenticated user's ID
   * @returns {Promise<Array<{key: string, url: string}>>} - Array of S3 keys and URLs
   * @throws {ContentPolicyError} when Rekognition rejects a file
   */
  async uploadPostImages(files, postId, userId) {
    if (!files || files.length === 0) throw new Error('No files provided');
    if (!postId) throw new Error('Post ID is required');
    if (!userId) throw new Error('User ID is required');

    const uploadPromises = files.map(async (file, index) => {
      // file may be a Blob (eagerly loaded on iOS) or a File
      const ext = file instanceof File
        ? file.name.split('.').pop()
        : (file.type.split('/')[1] || 'jpg');
      const name = file instanceof File ? file.name : `image_${index}.${ext}`;
      const timestamp = Date.now();
      // Core API's IDOR guard requires entityPath's first segment to be the
      // caller's own userId — postId just groups this post's images under it.
      const entityPath = `${userId}/${postId}/${timestamp}_${index}.${ext}`;

      // Goes through Core API → Rekognition moderation → S3 (same as all other asset types)
      const result = await uploadFile(file, 'posts_assets', name, entityPath);

      return { key: result.key, path: result.url, fileName: file.name };
    });

    return Promise.all(uploadPromises);
  }

  /**
   * Upload a single image for a post
   * @param {File} file - The image file to upload
   * @param {string} postId - The post's unique identifier
   * @param {string} userId - The authenticated user's ID
   * @returns {Promise<{key: string, url: string}>} - The S3 key and download URL
   */
  async uploadPostImage(file, postId, userId) {
    const results = await this.uploadPostImages([file], postId, userId);
    return results[0];
  }

  /**
   * Delete a post image
   * @param {string} key - The S3 key of the image to delete
   * @returns {Promise<void>}
   */
  async deletePostImage(key) {
    try {
    } catch (error) {
      console.error('Error deleting post image:', error);
      throw error;
    }
  }
}

export default new PostImageService();
